import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  getPatientCorrelationsInsights,
  getPatientMealsInsights,
  getPatientSelectedInsights,
  getPatientSymptomsInsights,
  getPatientWeeklyAverage,
} from "@/app/api/patient/insights";
import {
  IAverageInsights,
  ICorrelationsInsights,
  IFetchCorrelationsInsights,
  IFetchMealsInsights,
  IFetchSymptomsInsights,
  IMealsInsights,
  ISelectedInsights,
  ISymptomsInsights,
} from "@/app/api/patient/insights/types";
import { ErrorResponse } from "@/app/types/error";

type FetchStatus = "idle" | "loading" | "succeeded" | "failed";

interface InsightsState {
  statuses: Record<string, FetchStatus>;
  errors: Record<string, string | null>;
  mealsInsights: IMealsInsights | null;
  symptomsInsights: ISymptomsInsights | null;
  correlationsInsights: ICorrelationsInsights | null;
  averageInsights: IAverageInsights | null;
  selectedInsights: ISelectedInsights | null;
}

const initialState: InsightsState = {
  statuses: {
    meals: "loading",
    symptoms: "loading",
    correlations: "loading",
    average: "loading",
    selected: "loading",
  },
  errors: {
    meals: null,
    symptoms: null,
    correlations: null,
    average: null,
    selected: null,
  },
  mealsInsights: null,
  symptomsInsights: null,
  correlationsInsights: null,
  averageInsights: null,
  selectedInsights: null,
};

export const fetchMealsInsights = createAsyncThunk(
  "provider/patient/insights/meals",
  async (reqData: IFetchMealsInsights, { rejectWithValue }) => {
    try {
      const response = await getPatientMealsInsights(
        reqData.patientId,
        reqData.metadata,
        reqData.locale
      );

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const fetchSymptomsInsights = createAsyncThunk(
  "provider/patient/insights/symptoms",
  async (reqData: IFetchSymptomsInsights, { rejectWithValue }) => {
    try {
      const response = await getPatientSymptomsInsights(
        reqData.patientId,
        reqData.metadata,
        reqData.locale
      );

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const fetchCorrelationsInsights = createAsyncThunk(
  "provider/patient/insights/correlations",
  async (reqData: IFetchCorrelationsInsights, { rejectWithValue }) => {
    try {
      const response = await getPatientCorrelationsInsights(
        reqData.patientId,
        reqData.metadata,
        reqData.locale
      );

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const fetchAverageInsights = createAsyncThunk(
  "provider/patient/insights/average",
  async (patientId: string, { rejectWithValue }) => {
    try {
      const response = await getPatientWeeklyAverage(patientId);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const fetchSelectedInsights = createAsyncThunk(
  "provider/patient/insights/selected",
  async (patientId: string, { rejectWithValue }) => {
    try {
      const response = await getPatientSelectedInsights(patientId);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

const insightsSlice = createSlice({
  name: "insights",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMealsInsights.pending, (state) => {
        state.statuses["meals"] = "loading";
        state.errors["meals"] = null;
      })
      .addCase(fetchMealsInsights.rejected, (state, action) => {
        state.statuses["meals"] = "failed";
        state.errors["meals"] = action.error?.message as string;
      })
      .addCase(fetchMealsInsights.fulfilled, (state, action) => {
        state.statuses["meals"] = "succeeded";
        state.errors["meals"] = null;
        state.mealsInsights = action.payload as IMealsInsights;
      })
      .addCase(fetchSymptomsInsights.pending, (state) => {
        state.statuses["symptoms"] = "loading";
        state.errors["symptoms"] = null;
      })
      .addCase(fetchSymptomsInsights.rejected, (state, action) => {
        state.statuses["symptoms"] = "failed";
        state.errors["symptoms"] = action.error?.message as string;
      })
      .addCase(fetchSymptomsInsights.fulfilled, (state, action) => {
        state.statuses["symptoms"] = "succeeded";
        state.errors["symptoms"] = null;
        state.symptomsInsights = action.payload as ISymptomsInsights;
      })
      .addCase(fetchCorrelationsInsights.pending, (state) => {
        state.statuses["correlations"] = "loading";
        state.errors["correlations"] = null;
      })
      .addCase(fetchCorrelationsInsights.rejected, (state, action) => {
        state.statuses["correlations"] = "failed";
        state.errors["correlations"] = action.error?.message as string;
      })
      .addCase(fetchCorrelationsInsights.fulfilled, (state, action) => {
        state.statuses["correlations"] = "succeeded";
        state.errors["correlations"] = null;
        state.correlationsInsights = action.payload as ICorrelationsInsights;
      })
      .addCase(fetchAverageInsights.pending, (state) => {
        state.statuses["average"] = "loading";
        state.errors["average"] = null;
      })
      .addCase(fetchAverageInsights.rejected, (state, action) => {
        state.statuses["average"] = "failed";
        state.errors["average"] = action.error?.message as string;
      })
      .addCase(fetchAverageInsights.fulfilled, (state, action) => {
        state.statuses["average"] = "succeeded";
        state.errors["average"] = null;
        state.averageInsights = action.payload as IAverageInsights;
      })
      .addCase(fetchSelectedInsights.pending, (state) => {
        state.statuses["selected"] = "loading";
        state.errors["selected"] = null;
      })
      .addCase(fetchSelectedInsights.rejected, (state, action) => {
        state.statuses["selected"] = "failed";
        state.errors["selected"] = action.error?.message as string;
      })
      .addCase(fetchSelectedInsights.fulfilled, (state, action) => {
        state.statuses["selected"] = "succeeded";
        state.errors["selected"] = null;
        state.selectedInsights = action.payload as ISelectedInsights;
      });
  },
});

export const insightsReducer = insightsSlice.reducer;
